import * as React from "react";
import { TriangleAlert, Activity, CircleCheck, PhoneCall, Stethoscope, HeartPulse } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
const severityLevels = [{
  id: "emergency",
  title: "Gasping & Choking",
  description: "Waking up suddenly struggling for air.",
  actionText: "This is a hallmark sign of Obstructive Sleep Apnea (OSA). It indicates your airway has completely collapsed, forcing your brain to wake you up to resume breathing.",
  icon: TriangleAlert,
  colorTheme: "primary",
  bgClass: "bg-primary/5",
  borderClass: "border-primary/20",
  textClass: "text-primary",
  buttonText: "Symptom Details",
  buttonIcon: PhoneCall,
  buttonLink: "/SymptomsPage",
  buttonVariant: "default" as const
}, {
  id: "moderate",
  title: "Daytime Sleepiness",
  description: "Excessive fatigue despite a full night's sleep.",
  actionText: "Feeling unrefreshed or falling asleep during quiet moments suggests poor sleep quality. This lack of oxygen can lead to serious long-term health complications.",
  icon: Activity,
  colorTheme: "chart-3",
  bgClass: "bg-chart-3/10",
  borderClass: "border-chart-3/20",
  textClass: "text-chart-3",
  buttonText: "View Health Risks",
  buttonIcon: Stethoscope,
  buttonLink: "/HealthRisksPage",
  buttonVariant: "outline" as const
}, {
  id: "mild",
  title: "Chronic Snoring",
  description: "Loud, persistent snoring that disturbs others.",
  actionText: "While common, loud snoring is often the first indicator of airway resistance. Monitoring this early can prevent the progression of severe sleep disorders.",
  icon: CircleCheck,
  colorTheme: "chart-2",
  bgClass: "bg-chart-2/10",
  borderClass: "border-chart-2/20",
  textClass: "text-chart-2",
  buttonText: "Treatment Options",
  buttonIcon: HeartPulse,
  buttonLink: "/TreatmentsPage",
  buttonVariant: "outline" as const
}];
const faqs = [{
  question: "Is snoring always a sign of sleep apnea?",
  answer: "Not everyone who snores has sleep apnea, but it is one of the most common symptoms. If snoring is accompanied by silent pauses in breathing or gasping sounds, it is highly likely to be OSA and should be evaluated by a specialist."
}, {
  question: "How do I know if my fatigue is 'excessive'?",
  answer: "If you find yourself struggling to stay awake during driving, meetings, or reading, or if you feel a persistent 'brain fog' that affects your daily productivity, your sleep quality is likely being compromised by breathing interruptions."
}, {
  question: "When should I seek a professional consultation?",
  answer: "You should seek a consultation if you experience any combination of loud snoring, observed gasping, morning headaches, or high blood pressure. Early diagnosis is key to preventing heart disease and other systemic health issues."
}];
export default function CareSeverityGuide() {
  return <section data-section-id="154353" id="identifying-osa" className="w-full py-16 md:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="flex flex-col items-center text-center mb-12 space-y-4">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-primary font-sans">
            Identifying Sleep Apnea Symptoms
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg leading-relaxed">
            Recognizing the warning signs of Obstructive Sleep Apnea is the first step toward better health. 
            Review these key indicators to understand your risk and the necessary next steps.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {severityLevels.map((level, i) => {
          const Icon = level.icon;
          return <div data-index={i} key={level.id} className="h-full">
                <Card className={`h-full flex flex-col border ${level.bgClass} ${level.borderClass} shadow-sm transition-all duration-300 hover:shadow-lg rounded-xl`}>
                  <CardHeader className="pb-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 bg-background/80 backdrop-blur-sm border ${level.borderClass}`}>
                      <Icon className={`w-6 h-6 ${level.textClass}`} />
                    </div>
                    <CardTitle className={`text-2xl font-bold font-sans ${level.textClass}`}>
                      {level.title}
                    </CardTitle>
                    <CardDescription className="text-foreground/90 font-medium text-base">
                      {level.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <p className="text-muted-foreground text-base leading-relaxed">
                      {level.actionText}
                    </p>
                  </CardContent>
                  <CardFooter className="pt-4">
                    <Button asChild variant={level.buttonVariant} className="w-full rounded-lg font-semibold transition-colors">
                      <Link to={level.buttonLink}>
                        <level.buttonIcon className="w-4 h-4 mr-2" />
                        {level.buttonText}
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="mb-6 text-center">
            <h3 className="text-2xl md:text-3xl font-semibold text-primary font-sans">Understanding Your Risks</h3>
            <p className="text-muted-foreground mt-2 text-lg">Frequently asked questions about OSA symptoms and diagnosis.</p>
          </div>
          
          <Accordion type="single" collapsible className="w-full bg-card rounded-xl border border-border px-6 py-2 shadow-sm">
            {faqs.map((faq, i) => <div data-index={i} key={i}>
                <AccordionItem value={`item-${i}`} className="border-b last:border-0 border-border/50">
                  <AccordionTrigger className="text-lg font-semibold text-foreground hover:text-primary transition-colors py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </div>
      </div>
    </section>;
}